import { useCallback, useEffect, useRef, useState } from 'react';

interface ZoomableImageProps {
  src: string;
  alt: string;
  /** Max zoom multiplier above the "fit" baseline. Default 16x. */
  maxZoom?: number;
  /** Min zoom — usually 1 (fit-to-screen). Going below shrinks below fit. */
  minZoom?: number;
  /** Optional reset trigger — increment this to programmatically reset zoom (e.g. when src changes). */
  resetSignal?: unknown;
}

interface ViewState {
  scale: number;
  translateX: number;
  translateY: number;
}

const INITIAL: ViewState = { scale: 1, translateX: 0, translateY: 0 };

export function ZoomableImage({ src, alt, maxZoom = 16, minZoom = 1, resetSignal }: ZoomableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ lastX: number; lastY: number } | null>(null);
  const pinchRef = useRef<{ startDist: number; startScale: number; midX: number; midY: number } | null>(null);
  const [view, setView] = useState<ViewState>(INITIAL);
  const [animated, setAnimated] = useState(true);
  const [imageNatural, setImageNatural] = useState<{ w: number; h: number } | null>(null);

  // Compute the image's base displayed dimensions (after object-fit:contain at scale=1).
  // Used to clamp panning so the image edges never reveal whitespace beyond the container.
  function getDisplayedBase(): { w: number; h: number } | null {
    const c = containerRef.current;
    if (!c || !imageNatural) return null;
    const cw = c.clientWidth;
    const ch = c.clientHeight;
    const aspect = imageNatural.w / imageNatural.h;
    const containerAspect = cw / ch;
    if (aspect > containerAspect) {
      return { w: cw, h: cw / aspect };
    }
    return { w: ch * aspect, h: ch };
  }

  // Clamp a tentative view so the displayed image (at given scale) always fully covers
  // the visible viewport when scale >= 1 (no whitespace), and stays centered when scale=1.
  const clampView = useCallback(
    (v: ViewState): ViewState => {
      const c = containerRef.current;
      const base = getDisplayedBase();
      if (!c || !base) return v;
      const cw = c.clientWidth;
      const ch = c.clientHeight;
      const scaledW = base.w * v.scale;
      const scaledH = base.h * v.scale;
      const maxTx = Math.max(0, (scaledW - cw) / 2);
      const maxTy = Math.max(0, (scaledH - ch) / 2);
      return {
        scale: v.scale,
        translateX: Math.max(-maxTx, Math.min(maxTx, v.translateX)),
        translateY: Math.max(-maxTy, Math.min(maxTy, v.translateY)),
      };
    },
    // Re-derive bounds when image natural size becomes known
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageNatural],
  );

  // Streaming-fetch load with byte-progress so users see how much they have to wait for
  // (especially on the 230MB Bononiensis master, 110MB Hogenberg, etc.)
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [loaderVisible, setLoaderVisible] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Reset zoom state when src or resetSignal changes
  useEffect(() => {
    setView(INITIAL);
    setAnimated(true);
  }, [src, resetSignal]);

  // Streaming download of the image with progress tracking
  useEffect(() => {
    let cancelled = false;
    let createdBlobUrl: string | null = null;
    setBlobUrl(null);
    setProgress(null);
    setLoadError(null);

    // Delay loader appearance — small loads should not flash a progress bar
    const showLoaderTimer = setTimeout(() => {
      if (!cancelled) setLoaderVisible(true);
    }, 200);

    (async () => {
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const total = parseInt(res.headers.get('content-length') ?? '0', 10);
        const reader = res.body?.getReader();
        if (!reader) {
          // No streaming support: fallback to plain load via blob()
          const blob = await res.blob();
          if (cancelled) return;
          createdBlobUrl = URL.createObjectURL(blob);
          setBlobUrl(createdBlobUrl);
          setLoaderVisible(false);
          return;
        }
        const chunks: Uint8Array[] = [];
        let loaded = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (cancelled) {
            reader.cancel();
            return;
          }
          chunks.push(value);
          loaded += value.length;
          setProgress({ loaded, total });
        }
        if (cancelled) return;
        const blob = new Blob(chunks as BlobPart[]);
        createdBlobUrl = URL.createObjectURL(blob);
        setBlobUrl(createdBlobUrl);
        setLoaderVisible(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
        setLoaderVisible(false);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(showLoaderTimer);
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
    };
  }, [src]);

  const clampScale = useCallback(
    (s: number) => Math.max(minZoom, Math.min(maxZoom, s)),
    [maxZoom, minZoom],
  );

  // Wheel handler with non-passive listener (so preventDefault works)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      const delta = -e.deltaY * 0.0025;
      setView((prev) => {
        const newScale = Math.max(minZoom, Math.min(maxZoom, prev.scale * Math.exp(delta)));
        const factor = newScale / prev.scale;
        return clampView({
          scale: newScale,
          translateX: cx - (cx - prev.translateX) * factor,
          translateY: cy - (cy - prev.translateY) * factor,
        });
      });
      setAnimated(false);
    }
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [minZoom, maxZoom, clampView]);

  function onMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    dragRef.current = { lastX: e.clientX, lastY: e.clientY };
    setAnimated(false);
  }

  useEffect(() => {
    function onMove(e: MouseEvent) {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.lastX;
      const dy = e.clientY - d.lastY;
      d.lastX = e.clientX;
      d.lastY = e.clientY;
      setView((prev) => clampView({ ...prev, translateX: prev.translateX + dx, translateY: prev.translateY + dy }));
    }
    function onUp() {
      dragRef.current = null;
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  function onDoubleClick(e: React.MouseEvent<HTMLDivElement>) {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const cx = e.clientX - rect.left - rect.width / 2;
    const cy = e.clientY - rect.top - rect.height / 2;
    setAnimated(true);
    setView((prev) => {
      if (prev.scale > 1.5) return INITIAL;
      const newScale = clampScale(prev.scale * 2.5);
      const factor = newScale / prev.scale;
      return clampView({
        scale: newScale,
        translateX: cx - (cx - prev.translateX) * factor,
        translateY: cy - (cy - prev.translateY) * factor,
      });
    });
  }

  // Touch handlers (pinch-zoom + single-finger pan)
  function onTouchStart(e: React.TouchEvent) {
    if (e.touches.length === 1) {
      dragRef.current = { lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };
      setAnimated(false);
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const container = containerRef.current;
      const rect = container?.getBoundingClientRect();
      const midX = ((e.touches[0].clientX + e.touches[1].clientX) / 2) - (rect?.left ?? 0) - (rect?.width ?? 0) / 2;
      const midY = ((e.touches[0].clientY + e.touches[1].clientY) / 2) - (rect?.top ?? 0) - (rect?.height ?? 0) / 2;
      pinchRef.current = { startDist: dist, startScale: view.scale, midX, midY };
      dragRef.current = null;
      setAnimated(false);
    }
  }

  function onTouchMove(e: React.TouchEvent) {
    if (e.touches.length === 1 && dragRef.current) {
      const dx = e.touches[0].clientX - dragRef.current.lastX;
      const dy = e.touches[0].clientY - dragRef.current.lastY;
      dragRef.current.lastX = e.touches[0].clientX;
      dragRef.current.lastY = e.touches[0].clientY;
      setView((prev) => clampView({ ...prev, translateX: prev.translateX + dx, translateY: prev.translateY + dy }));
    } else if (e.touches.length === 2 && pinchRef.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / pinchRef.current.startDist;
      const newScale = clampScale(pinchRef.current.startScale * ratio);
      const { midX, midY, startScale } = pinchRef.current;
      const factor = newScale / startScale;
      setView((prev) => clampView({
        scale: newScale,
        translateX: midX - (midX - prev.translateX) * factor,
        translateY: midY - (midY - prev.translateY) * factor,
      }));
    }
  }

  function onTouchEnd(e: React.TouchEvent) {
    if (e.touches.length === 0) {
      dragRef.current = null;
      pinchRef.current = null;
    } else if (e.touches.length === 1) {
      pinchRef.current = null;
      dragRef.current = { lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };
    }
  }

  function zoomBy(factor: number) {
    setAnimated(true);
    setView((prev) => {
      const newScale = clampScale(prev.scale * factor);
      const f = newScale / prev.scale;
      return clampView({
        scale: newScale,
        translateX: prev.translateX * f,
        translateY: prev.translateY * f,
      });
    });
  }

  function reset() {
    setAnimated(true);
    setView(INITIAL);
  }

  return (
    <div
      ref={containerRef}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      data-testid="zoomable-image"
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        cursor: view.scale > 1 ? (dragRef.current ? 'grabbing' : 'grab') : 'zoom-in',
        userSelect: 'none',
        background: '#fffdf2',
        borderRadius: 'var(--radius-sm)',
        touchAction: 'none',
      }}
    >
      {blobUrl ? (
        <img
          ref={imgRef}
          src={blobUrl}
          alt={alt}
          draggable={false}
          onLoad={(e) => {
            const target = e.currentTarget;
            if (target.naturalWidth && target.naturalHeight) {
              setImageNatural({ w: target.naturalWidth, h: target.naturalHeight });
            }
          }}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            transform: `translate(-50%, -50%) translate(${view.translateX}px, ${view.translateY}px) scale(${view.scale})`,
            transformOrigin: 'center',
            transition: animated ? 'transform 140ms cubic-bezier(0.22, 0.61, 0.36, 1)' : 'none',
            pointerEvents: 'none',
            imageRendering: view.scale > 4 ? 'pixelated' : 'auto',
          }}
        />
      ) : null}

      {/* Streaming-load progress overlay — Grunk logo fills bottom-up as bytes arrive */}
      {loaderVisible && !blobUrl ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(20, 16, 12, 0.92)',
            backdropFilter: 'blur(8px)',
            color: '#fffdf2',
            fontFamily: 'var(--font-body)',
            zIndex: 5,
            gap: 24,
          }}
          data-testid="image-loading-overlay"
        >
          {(() => {
            const pct = progress && progress.total > 0 ? Math.min(100, (progress.loaded / progress.total) * 100) : 0;
            const indeterminate = !progress || progress.total === 0;
            return (
              <div
                style={{
                  position: 'relative',
                  width: 180,
                  height: 180,
                }}
                aria-label="loading"
                role="img"
              >
                {/* Empty (white outline) logo — always visible */}
                <img
                  src="/img/logo/grunk-empty.png"
                  alt=""
                  draggable={false}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    userSelect: 'none',
                    opacity: 0.85,
                  }}
                />
                {/* Colored (full) logo — clipped from bottom up by progress */}
                <img
                  src="/img/logo/grunk-full.png"
                  alt=""
                  draggable={false}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    userSelect: 'none',
                    clipPath: indeterminate
                      ? 'inset(75% 0 0 0)'
                      : `inset(${Math.max(0, 100 - pct)}% 0 0 0)`,
                    transition: indeterminate ? 'none' : 'clip-path 200ms ease-out',
                    animation: indeterminate ? 'grunk-indeterminate 1.6s ease-in-out infinite' : 'none',
                  }}
                />
              </div>
            );
          })()}

          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: '1rem', letterSpacing: '-0.01em' }}>
              Loading map
            </div>
            <div
              style={{
                fontSize: '0.85rem',
                fontFamily: 'ui-monospace, monospace',
                color: 'var(--text-muted, #9c9286)',
                letterSpacing: '0.02em',
              }}
              data-testid="loading-progress"
            >
              {progress
                ? progress.total > 0
                  ? `${fmtMB(progress.loaded)} / ${fmtMB(progress.total)}  ·  ${Math.round((progress.loaded / progress.total) * 100)}%`
                  : `${fmtMB(progress.loaded)} downloaded`
                : 'starting…'}
            </div>
          </div>

          <style>{`
            @keyframes grunk-indeterminate {
              0%   { clip-path: inset(95% 0 0 0); }
              50%  { clip-path: inset(20% 0 0 0); }
              100% { clip-path: inset(95% 0 0 0); }
            }
          `}</style>
        </div>
      ) : null}

      {/* Load error */}
      {loadError && !blobUrl ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(20, 16, 12, 0.94)',
            color: '#d4756a',
            fontFamily: 'var(--font-body)',
            fontSize: '0.85rem',
            padding: 'var(--space-4)',
            textAlign: 'center',
          }}
        >
          Failed to load image: {loadError}
        </div>
      ) : null}

      {/* Zoom controls overlay */}
      <div
        style={{
          position: 'absolute',
          bottom: 'var(--space-3)',
          right: 'var(--space-3)',
          display: 'flex',
          gap: 4,
          background: 'rgba(10, 8, 7, 0.78)',
          backdropFilter: 'blur(10px)',
          padding: '4px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid rgba(255, 253, 242, 0.12)',
        }}
      >
        <button
          type="button"
          onClick={() => zoomBy(1 / 1.6)}
          aria-label="Zoom out"
          style={zoomBtnStyle}
        >
          −
        </button>
        <div
          style={{
            padding: '4px 10px',
            color: '#fffdf2',
            fontFamily: 'ui-monospace, monospace',
            fontSize: '0.75rem',
            minWidth: 56,
            textAlign: 'center',
            lineHeight: '20px',
          }}
          data-testid="zoom-level"
        >
          {Math.round(view.scale * 100)}%
        </div>
        <button
          type="button"
          onClick={() => zoomBy(1.6)}
          aria-label="Zoom in"
          style={zoomBtnStyle}
        >
          +
        </button>
        <button
          type="button"
          onClick={reset}
          aria-label="Reset zoom"
          disabled={view.scale === 1 && view.translateX === 0 && view.translateY === 0}
          style={{ ...zoomBtnStyle, opacity: view.scale === 1 && view.translateX === 0 && view.translateY === 0 ? 0.4 : 1 }}
        >
          ⌂
        </button>
      </div>

      {/* Hint overlay (only at 1x) */}
      {view.scale === 1 && view.translateX === 0 && view.translateY === 0 ? (
        <div
          style={{
            position: 'absolute',
            top: 'var(--space-3)',
            left: 'var(--space-3)',
            color: 'rgba(10, 8, 7, 0.55)',
            background: 'rgba(255, 253, 242, 0.85)',
            backdropFilter: 'blur(8px)',
            padding: '4px 10px',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.72rem',
            fontFamily: 'ui-monospace, monospace',
            pointerEvents: 'none',
          }}
        >
          scroll to zoom · drag to pan · double-click to zoom
        </div>
      ) : null}
    </div>
  );
}

const zoomBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  background: 'transparent',
  color: '#fffdf2',
  border: 'none',
  fontSize: '1rem',
  cursor: 'pointer',
  borderRadius: 4,
  lineHeight: '20px',
  padding: 0,
};

function fmtMB(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
